export type StyleFieldType =
  | 'color'
  | 'image'
  | 'url'
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'select';

/**
 * Default copy for the entry-confirmation popup body when a contest has not
 * set `entry.confirmation_message`. Shared by the admin schema hint and the
 * frontend so the fallback text stays in one place.
 */
export const DEFAULT_ENTRY_CONFIRMATION_MESSAGE =
  'Please confirm your entry into this contest. You can edit your picks anytime up until start of the first game.';

/**
 * Label for the T&C link beside the entry-confirmation checkbox when a contest
 * sets no `entry.terms_label`. Brand-neutral by design: this was the
 * incumbent's own label hardcoded beside a hardcoded link to their terms
 * document, shown on every operator's instance (CAE-659).
 */
export const DEFAULT_TERMS_LABEL = 'the contest terms and conditions';

export interface StyleFieldDef {
  key: string;
  label: string;
  type: StyleFieldType;
  default?: string | number | boolean;
  options?: { value: string; label: string }[];
  uploadPath?: string;
  description?: string;
  /** When set, the field is only shown when the sibling field matches the value. */
  dependsOn?: { field: string; value: StyleFieldValue };
}

export interface StyleSection {
  title: string;
  key: string;
  fields: StyleFieldDef[];
}

export interface StyleSchema {
  sections: StyleSection[];
}

export type GameType = 'pickem' | 'squares' | 'daily-reveal' | 'squads';

export type StyleFieldValue = string | number | boolean;
export type StyleConfig = Record<string, Record<string, StyleFieldValue>>;

export const pickemStyleSchema: StyleSchema = {
  sections: [
    {
      title: 'Header',
      key: 'header',
      fields: [
        {
          key: 'enabled',
          label: 'Show header banner',
          type: 'boolean',
          default: false,
          description:
            'When disabled, the banner is hidden and the info icon moves to the contest card.',
        },
        {
          // DKPLAT-664. Deliberately NOT gated on `enabled`: it applies whether
          // the banner is shown or not — the menu replaces whichever info icon
          // is currently visible (banner when enabled, contest card when not).
          key: 'showOverflowMenu',
          label: 'Use three-dot contest menu',
          type: 'boolean',
          default: false,
          description:
            'Replaces the info icon with a three-dot menu holding Quick Pick, Clear Selection and Contest Details (plus Popular Pick when enabled), and removes the separate Quick Pick/Clear row so more questions fit on screen.',
        },
        {
          key: 'logo',
          label: 'Logo',
          type: 'image',
          uploadPath: 'pickems/{contestId}/header-logo',
          dependsOn: { field: 'enabled', value: true },
        },
        {
          key: 'background_image',
          label: 'Background image',
          type: 'image',
          uploadPath: 'pickems/{contestId}/header-bg',
          dependsOn: { field: 'enabled', value: true },
        },
        {
          key: 'gradient_start',
          label: 'Gradient start',
          type: 'color',
          dependsOn: { field: 'enabled', value: true },
        },
        {
          key: 'gradient_end',
          label: 'Gradient end',
          type: 'color',
          dependsOn: { field: 'enabled', value: true },
        },
      ],
    },
    {
      title: 'Game Card',
      key: 'game_card',
      fields: [
        {
          key: 'bg_gradient_start',
          label: 'Gradient start',
          type: 'color',
        },
        {
          key: 'bg_gradient_end',
          label: 'Gradient end',
          type: 'color',
        },
        {
          key: 'border_color',
          label: 'Border colour',
          type: 'color',
        },
        {
          key: 'question_number_bg',
          label: 'Question number background',
          type: 'color',
        },
        {
          key: 'question_number_fg',
          label: 'Question number foreground',
          type: 'color',
        },
      ],
    },
    {
      title: 'Daily Double',
      key: 'daily_double',
      fields: [
        {
          key: 'card_gradient_start',
          label: 'Card gradient start',
          type: 'color',
        },
        {
          key: 'card_gradient_end',
          label: 'Card gradient end',
          type: 'color',
        },
        {
          key: 'card_border_color',
          label: 'Card border colour',
          type: 'color',
        },
        {
          key: 'number_badge_bg',
          label: 'Number badge background',
          type: 'color',
        },
        {
          key: 'number_badge_fg',
          label: 'Number badge foreground',
          type: 'color',
        },
        {
          key: 'button_bg',
          label: 'Button background',
          type: 'color',
        },
        {
          key: 'button_border',
          label: 'Button border',
          type: 'color',
        },
        {
          key: 'background_image',
          label: 'Background image',
          type: 'image',
          uploadPath: 'pickems/{contestId}/dd-background',
        },
        {
          key: 'logo_image',
          label: 'Logo image',
          type: 'image',
          uploadPath: 'pickems/{contestId}/dd-logo',
        },
        {
          key: 'secondary_image',
          label: 'Secondary image',
          type: 'image',
          uploadPath: 'pickems/{contestId}/dd-secondary',
        },
      ],
    },
    {
      title: 'Contest Card',
      key: 'contest_card',
      fields: [
        {
          key: 'bg_gradient_start',
          label: 'Background gradient start',
          type: 'color',
        },
        {
          key: 'bg_gradient_end',
          label: 'Background gradient end',
          type: 'color',
        },
        {
          key: 'border_color',
          label: 'Border colour',
          type: 'color',
        },
      ],
    },
    {
      title: 'Logos',
      key: 'logos',
      fields: [
        {
          key: 'confirmation_logo',
          label: 'Confirmation logo',
          type: 'image',
          uploadPath: 'pickems/{contestId}/confirmation-logo',
        },
      ],
    },
    {
      title: 'Entry',
      key: 'entry',
      fields: [
        {
          key: 'submit_message',
          label: 'Submit reminder message',
          type: 'text',
          description:
            'Optional reminder shown above the Submit Picks button (e.g. "Make sure to submit your picks before the end of your flight."). Leave blank to hide.',
        },
        {
          key: 'confirmation_message',
          label: 'Entry confirmation message',
          type: 'textarea',
          description:
            'Shown in the entry-confirmation popup above the checkbox. Add a link with [text](https://example.com). ' +
            `Leave blank to use the default: "${DEFAULT_ENTRY_CONFIRMATION_MESSAGE}"`,
        },
        {
          key: 'terms_url',
          label: 'Terms & conditions URL',
          type: 'text',
          description:
            'Where the T&C link beside the confirmation checkbox points — your own terms document. ' +
            'Leave blank and the label shows as plain text: no instance links a player to another operator’s terms (CAE-659).',
        },
        {
          key: 'terms_label',
          label: 'Terms & conditions link text',
          type: 'text',
          description: `Wording of that link, e.g. "Pigskin Pick'em contest T&Cs". Leave blank to use "${DEFAULT_TERMS_LABEL}".`,
        },
      ],
    },
  ],
};

export const DAILY_REVEAL_DEFAULT_MAIN_HEADER_LOGO =
  '/Big-League-Draw-Logo-Horizontal_Resized.png';
export const DAILY_REVEAL_DEFAULT_NFL_MAIN_HEADER_LOGO = '/NFL-Reveal/Gridiron-Draw-Logo.png';
export const DAILY_REVEAL_DEFAULT_REVEAL_BACKGROUND = '/ball-park-2.png';
export const DAILY_REVEAL_DEFAULT_NFL_REVEAL_BACKGROUND =
  '/NFL-Reveal/AdobeStock_483817817-1.png';

export type DailyRevealBrandingStyleConfig = {
  main_header_logo?: string;
  reveal_background?: string;
};

export const dailyRevealStyleSchema: StyleSchema = {
  sections: [
    {
      title: 'Branding',
      key: 'branding',
      fields: [
        {
          key: 'main_header_logo',
          label: 'Main header logo',
          type: 'image',
          uploadPath: 'pickems/{contestId}/daily-reveal-header-logo',
          description:
            'Top-of-page logo on the app entry and reveal screens. Overrides the default contest logo when set.',
        },
        {
          key: 'reveal_background',
          label: 'Reveal background',
          type: 'image',
          uploadPath: 'pickems/{contestId}/daily-reveal-background',
          description:
            'Full-screen background on the reveal flow (e.g. ballpark diamond). Overrides the default reveal background when set.',
        },
      ],
    },
  ],
};

/** Minimal Squads styling surface — full schema lands with the admin styling ticket (CSQ-D14). */
export const squadsStyleSchema: StyleSchema = {
  sections: [],
};

export const styleSchemas: Partial<Record<GameType, StyleSchema>> = {
  pickem: pickemStyleSchema,
  'daily-reveal': dailyRevealStyleSchema,
  squads: squadsStyleSchema,
};

/** Resolve Daily Reveal branding assets with local fallbacks when unset. */
export function resolveDailyRevealBrandingStyle(
  styleConfig: Record<string, unknown> | null | undefined,
  sport?: string | null
): {
  mainHeaderLogoUrl: string;
  revealBackgroundUrl: string;
} {
  const branding = (styleConfig?.branding ?? {}) as DailyRevealBrandingStyleConfig;
  const sportCode = String(sport ?? '').trim().toUpperCase();
  const defaultMainHeaderLogoUrl =
    sportCode === 'NFL'
      ? DAILY_REVEAL_DEFAULT_NFL_MAIN_HEADER_LOGO
      : DAILY_REVEAL_DEFAULT_MAIN_HEADER_LOGO;
  const defaultRevealBackgroundUrl =
    sportCode === 'NFL'
      ? DAILY_REVEAL_DEFAULT_NFL_REVEAL_BACKGROUND
      : DAILY_REVEAL_DEFAULT_REVEAL_BACKGROUND;
  const mainHeaderLogoUrl =
    String(branding.main_header_logo ?? '').trim() || defaultMainHeaderLogoUrl;
  const revealBackgroundUrl =
    String(branding.reveal_background ?? '').trim() || defaultRevealBackgroundUrl;
  return { mainHeaderLogoUrl, revealBackgroundUrl };
}

export function getStyleSchema(gameType: GameType): StyleSchema | undefined {
  return styleSchemas[gameType];
}

export function defaultStyleConfig(gameType: GameType): StyleConfig {
  const schema = getStyleSchema(gameType);
  if (!schema) return {};
  const out: StyleConfig = {};
  for (const section of schema.sections) {
    for (const field of section.fields) {
      if (field.default !== undefined) {
        if (!out[section.key]) out[section.key] = {};
        out[section.key][field.key] = field.default;
      }
    }
  }
  return out;
}

export type PickemHeaderStyleConfig = Record<string, StyleFieldValue>;

function headerHasConfiguredAssets(header: PickemHeaderStyleConfig): boolean {
  const hasLogo = Boolean(String(header.logo ?? '').trim());
  const hasBg = Boolean(String(header.background_image ?? '').trim());
  const hasGradient = Boolean(
    String(header.gradient_start ?? '').trim() || String(header.gradient_end ?? '').trim()
  );
  return hasLogo || hasBg || hasGradient;
}

/** Coerce persisted header.enabled values (boolean, string, etc.) for comparisons. */
export function parsePickemHeaderEnabled(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === 'true' || value === '1') return true;
  if (value === false || value === 0 || value === 'false' || value === '0') return false;
  return undefined;
}

/**
 * Normalize header config for admin forms so the toggle matches what players see.
 * Legacy pickems may have banner assets but no `enabled` key — treat those as on.
 */
export function normalizePickemHeaderConfigForForm(
  header: PickemHeaderStyleConfig | null | undefined
): PickemHeaderStyleConfig {
  if (!header) return { enabled: false };

  const enabled = parsePickemHeaderEnabled(header.enabled);
  if (enabled === true || enabled === false) {
    return { ...header, enabled };
  }

  return { ...header, enabled: headerHasConfiguredAssets(header) };
}

/**
 * Whether the pickem detail/lobby header banner should render.
 * When disabled or empty, no fallback assets are shown (DKPLAT-437).
 */
export function isPickemHeaderVisible(
  header: PickemHeaderStyleConfig | null | undefined
): boolean {
  if (!header) return false;

  const enabled = parsePickemHeaderEnabled(header.enabled);
  if (enabled === false) return false;
  if (enabled === true) return true;

  // Legacy pickems created before the toggle existed: only show when assets exist.
  return headerHasConfiguredAssets(header);
}
